import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Empty } from "@/components/ui-kit";
import { Pager, fmtTime, todayStr } from "@/components/Pager";
import { AttachmentList } from "@/components/Attachments";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useParentLang, LangSwitch } from "@/lib/parentLang";
import { BookOpenText, CheckCircle2, Eye, MessageCircle, PartyPopper, Megaphone, CalendarCheck } from "lucide-react";

const SECTIONS = ["topics", "objectives", "activities", "homework", "worksheets", "announcements"];
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toLocaleDateString("en-CA"); };

function DiaryEntry({ d, t, studentId, onSeen }) {
  async function seen() {
    try { await api.post(`/diaries/${d.id}/ack`, { student_id: studentId }); toast.success(t.seen); onSeen(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div data-testid={`parent-diary-${d.id}`} className="bg-white rounded-2xl shadow-sm p-4 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-lg font-bold text-slate-900">{d.subject}</span>
        <span className="text-xs text-slate-500">{new Date(`${d.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })} • {d.teacher_name}</span>
        <div className="ml-auto">
          {d.my_ack
            ? <span data-testid={`parent-diary-seen-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1"><CheckCircle2 size={14} /> {t.seenAt} {fmtTime(d.my_ack.seen_at)}</span>
            : <Button size="sm" data-testid={`parent-diary-ack-${d.id}`} onClick={seen} className="btn-brand"><Eye size={14} className="mr-1" /> {t.markSeen}</Button>}
        </div>
      </div>
      {SECTIONS.filter((k) => d[k]).map((k) => (
        <div key={k}><div className="text-[11px] uppercase tracking-wide font-semibold text-violet-700">{t[k]}</div><p className="text-sm text-slate-700 whitespace-pre-line">{d[k]}</p></div>
      ))}
      {d.student_remarks?.map((r) => <div key={r.student_id} className="text-sm bg-amber-50 rounded-xl p-2.5"><b>{t.remark}:</b> {r.remark}</div>)}
      <AttachmentList items={d.attachments} />
    </div>
  );
}

function SideCards({ child, t }) {
  const [att, setAtt] = useState(null);
  const [events, setEvents] = useState([]);
  const [notices, setNotices] = useState([]);
  useEffect(() => {
    const n = new Date();
    if (child) api.get("/parent/attendance", { params: { student_id: child.id, year: n.getFullYear(), month: n.getMonth() + 1 } }).then((r) => setAtt(r.data)).catch(() => setAtt(null));
  }, [child]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.date >= todayStr()).slice(0, 4))).catch(() => {});
    api.get("/circulars").then((r) => setNotices(r.data.slice(0, 3))).catch(() => {});
  }, []);
  const st = att?.days?.[todayStr()];
  const map = { Present: t.present, Absent: t.absent, Late: t.late, Leave: t.leave };
  return (
    <div className="space-y-4">
      <div data-testid="parent-attendance-card" className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex items-center gap-2 font-bold mb-2"><CalendarCheck size={17} className="text-violet-700" /> {t.attendance}</div>
        <div className="text-sm">{t.todayStatus}: <b className={st === "Absent" ? "text-rose-700" : "text-emerald-700"}>{st ? map[st] : t.notMarked}</b></div>
        {att && <div className="text-xs text-slate-500 mt-1">{t.month}: {t.present} {att.counts.Present} • {t.absent} {att.counts.Absent} • {t.late} {att.counts.Late} • {t.leave} {att.counts.Leave}</div>}
      </div>
      <div data-testid="parent-events-card" className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex items-center gap-2 font-bold mb-2"><PartyPopper size={17} className="text-violet-700" /> {t.events}</div>
        {events.length === 0 ? <p className="text-xs text-slate-500">{t.noEvents}</p> : events.map((e) => <div key={e.id} className="text-sm py-0.5"><b>{new Date(`${e.date}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</b> {e.title}</div>)}
      </div>
      <div data-testid="parent-notices-card" className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex items-center gap-2 font-bold mb-2"><Megaphone size={17} className="text-violet-700" /> {t.notices}</div>
        {notices.length === 0 ? <p className="text-xs text-slate-500">{t.noNotices}</p> : notices.map((c) => <div key={c.id} className="text-sm py-1 border-b last:border-0 border-slate-100"><b>{c.title}</b><p className="text-xs text-slate-600 line-clamp-2">{c.body}</p></div>)}
      </div>
    </div>
  );
}

export default function ParentDiaryPage() {
  const { t } = useParentLang();
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [today, setToday] = useState([]);
  const [prev, setPrev] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [range, setRange] = useState({ from: "", to: yesterday() });
  const [page, setPage] = useState(1);
  useEffect(() => { api.get("/parent/children").then((r) => { setChildren(r.data); setChild(r.data[0] || null); }).catch(() => {}); }, []);
  const loadToday = () => child && api.get("/diaries", { params: { student_id: child.id, day: todayStr(), limit: 50 } }).then((r) => setToday(r.data.items)).catch(() => {});
  const loadPrev = () => child && api.get("/diaries", { params: { student_id: child.id, date_from: range.from || undefined, date_to: range.to || yesterday(), page, limit: 10 } }).then((r) => setPrev(r.data)).catch(() => {});
  useEffect(() => { loadToday(); }, [child]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPrev(); }, [child, range, page]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppShell>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-1">{child ? `${child.name} • ${child.class_name}-${child.section}` : ""}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{t.title}</h1>
          <p className="text-slate-500 mt-1">{t.sub}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <LangSwitch />
          <Button asChild className="btn-brand" data-testid="parent-message-teacher"><Link to="/messages"><MessageCircle size={15} className="mr-1" /> {t.message}</Link></Button>
        </div>
      </div>
      {children.length > 1 && (
        <div className="flex gap-2 mb-5 overflow-x-auto">
          {children.map((c) => (
            <button key={c.id} data-testid={`parent-child-${c.id}`} onClick={() => { setChild(c); setPage(1); }}
              className={`flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border transition-colors ${child?.id === c.id ? "bg-violet-600 text-white border-violet-600" : "bg-white border-violet-100"}`}>
              {c.photo_url ? <AuthImage src={c.photo_url} alt={c.name} className="h-8 w-8 rounded-full object-cover" /> : <span className="h-8 w-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold">{c.name[0]}</span>}
              <span className="text-sm font-semibold">{c.name}</span>
            </button>
          ))}
        </div>
      )}
      {!child ? <Empty testid="parent-no-child" icon={BookOpenText} text="No child linked to your account yet." /> : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-5">
          <div className="space-y-6">
            <section><h2 className="font-display font-bold text-slate-900 mb-3">{t.today}</h2>
              {today.length === 0 ? <p data-testid="parent-diary-none" className="text-sm text-slate-500 bg-white rounded-2xl p-4">{t.none}</p>
                : <div className="space-y-3">{today.map((d) => <DiaryEntry key={d.id} d={d} t={t} studentId={child.id} onSeen={loadToday} />)}</div>}
            </section>
            <section>
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <h2 className="font-display font-bold text-slate-900 mr-auto">{t.prev}</h2>
                <span className="text-xs">{t.from}</span><Input data-testid="parent-prev-from" type="date" value={range.from} onChange={(e) => { setRange({ ...range, from: e.target.value }); setPage(1); }} className="w-40 bg-white" />
                <span className="text-xs">{t.to}</span><Input data-testid="parent-prev-to" type="date" value={range.to} onChange={(e) => { setRange({ ...range, to: e.target.value }); setPage(1); }} className="w-40 bg-white" />
              </div>
              {prev.items.length === 0 ? <p className="text-sm text-slate-500 bg-white rounded-2xl p-4">{t.noPrev}</p>
                : <div className="space-y-3">{prev.items.map((d) => <DiaryEntry key={d.id} d={d} t={t} studentId={child.id} onSeen={loadPrev} />)}</div>}
              <Pager page={prev.page} pages={prev.pages} total={prev.total} onPage={setPage} testid="parent-prev-pager" />
            </section>
          </div>
          <SideCards child={child} t={t} />
        </div>
      )}
    </AppShell>
  );
}
